import * as React from 'react';
import {
  fetchLastActivityTrackingEvent,
  addManualTrackingEvent,
} from 'src/api/shipmentTracking';
import moment from 'moment';
import { Button, Form, Modal, message, Row, Col, Input, DatePicker } from 'antd';
import Select from 'src/components/common/Select';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import injectSheet from 'react-jss';
import { fetchManualTrackingMaster } from '../../../actions/activityMasterDataActions';
import { SHIPMENT_TYPE } from './ContainerUtils';
import SearchPorts from './SearchPorts';
import { MOBILE_BREAK_POINT } from 'src/globals';
import { FormComponentProps } from 'antd/lib/form';
import { isValidArray } from 'src/Library/Helper';
import { StylesProps, ShippingLine } from 'src/Library/Types';
import { tokens } from 'src/design-system';
import { commonStyles } from 'src/Library/CommonStyles';

const { Option } = Select;
const styles = theme => ({
  ...commonStyles(theme),
  formLabel: {
    fontSize: tokens.fontSize.small,
    fontWeight: tokens.fontWeight.semibold,
    lineHeight: '40px',
  },
  required: {
    color: tokens.colors.error,
  },
  modalNote: {
    fontStyle: 'italic',
    color: tokens.colors.textTertiary,
    fontSize: tokens.fontSize.small,
    marginTop: '25px',
    marginBottom: '0px',
  },
  modalBodyTitle: {
    fontSize: tokens.fontSize.small,
    fontWeight: tokens.fontWeight.semibold,
    margin: '0px',
  },
  modalSubmitButton: {
    backgroundColor: tokens.colors.primary,
    borderRadius: tokens.radius.sm,
    color: tokens.colors.bgWhite,
    fontSize: tokens.fontSize.h3,
    height: '40px',
    width: '94px',
  },
  [`@media (max-width: ${MOBILE_BREAK_POINT}px)`]: {
    modal: {
      '& .ant-modal-content': {
        width: '520px',
      }},
  },
});

interface IProps extends FormComponentProps, StylesProps<ReturnType<typeof styles>>, ReduxProps {
  shipmentId: string;
  shipmentNumber: string;
  shipmentType: string;
  onClose: (success?: boolean) => void;
  activityMasterData : any;
  shipmentShippingLine: ShippingLine;
  visible: boolean;
}

interface ReduxProps {
  staticData: any;
  activityMasterData: any;
  fetchManualTrackingMaster: (params: any) => any;
}
interface StateProps {
  isPrefillingLastActivity: boolean;
  confirmLoading:  boolean;
  isVoyageDetails:  boolean;
}
const  AddManualTrackingEventModal = (props: IProps) => {

  const [state, setStateUtil] = React.useState<StateProps>({
    isPrefillingLastActivity: false,
    confirmLoading: false,
    isVoyageDetails: false,
  });

  function setState(newState: Partial<StateProps>) {
    setStateUtil((oldState: StateProps) => {
      return {
        ...oldState,
        ...newState,
      };
    });
  }

  React.useEffect(() => {
    loadData();
  }, []);

  const {
    form,
    shipmentNumber,
    shipmentId,
    classes,
    staticData,
    onClose,
    shipmentType,
    shipmentShippingLine,
    visible,
    fetchManualTrackingMaster,
    activityMasterData : { activityMaster },
  } = props;

  const {
    isPrefillingLastActivity,
    confirmLoading,
    isVoyageDetails,
  } = state;

  function disabledFutureDate(current: moment.Moment) {
    return current && current > moment().endOf('day');
  }

  const getShipmentTypeForApi = () => {
    if (shipmentType === SHIPMENT_TYPE.BK) return SHIPMENT_TYPE.BL;
    return shipmentType;
  };

  async function loadData() {
    setState({
      isPrefillingLastActivity: true,
    });

    if (!activityMaster || Object.keys(activityMaster).length === 0) {
      await fetchManualTrackingMaster({ shipmentId, shipmentType: getShipmentTypeForApi() });
    }
    // fetch last activity
    const response = await fetchLastActivityTrackingEvent({
      shipmentId,
      shipmentType: getShipmentTypeForApi(),
    });
    if (response.isSuccess) {
      prefillData(response.data);
    } else {
      message.error(response.errorMessage);
    }
    setState({
      isPrefillingLastActivity: false,
    });
  }

  function prefillData(data) {
    if (!data || !Object.keys(data).length) return;
    if (data.vehicleType === 'Vessel') {
      setState({ isVoyageDetails: true });
    }
    form.setFieldsValue({
      vehicleType: data.vehicleType || undefined,
      locationType: data.locationType || undefined,
      vehicleNumber: data.vehicleNumber || undefined,
      vehicleName: data.vehicleName || undefined,
      location: data.location && data.locationCode ? {
        key: data.locationCode,
        label: data.location.concat(' (', `${data.locationCode}`, ')'),
      } : undefined,
    });
  }

  function checkForErrors() {
    form.validateFields((err, values) => {
      if (err && Object.keys(err).length) {
        message.error('Please fill all required fields');
        return;
      }
      prepareRequestData(values);
    });
  }

  async function prepareRequestData(formData) {
    const {
      location,
      activityDate,
    } = formData;
    const requestData: any = {
      activities : [],
    };
    const activityObject = {
      ...formData,
      shipmentId,
      shipmentType: getShipmentTypeForApi(),
    };
     // extracting name from label
    activityObject.location = location.label.substr(0, location.label.lastIndexOf(' ('));
    activityObject.locationCode = location.key;
    activityObject.activityTimestamp = moment(activityDate).unix() * 1000;
    requestData.activities.push(activityObject);
    await sendRequestData(requestData);
  }

  async function sendRequestData(requestData) {
    setState({ confirmLoading: true });
    const response = await addManualTrackingEvent(requestData);
    setState({ confirmLoading: false });
    if (response.isSuccess) {
      if (response.data && isValidArray(response.data.failureArray)) {
        message.error(response.data.failureArray[0].error_message);
        return;
      }
      message.success('Tracking Event has been added succesfully');
      onClose(true);
    } else {
      message.error(response.errorMessage);
    }
  }

  const renderMessage = () => {
    if (shipmentType !== SHIPMENT_TYPE.BL) return null;
    return <p className={classes.modalNote}>
      Note: Tracking event will be added to all the containers.
    </p>;
  };

  const renderVoyage = () => {
    if (!isVoyageDetails) return null;
    return <Row>
      <Col span={4} style={{ fontSize: '12px', fontWeight: 600 }}>
        Voyage Details
      </Col>
      <Col span={10} style={{ paddingRight: '6.5px' }}>
        <Form.Item
          className={classes.formItem}
        >
          {form.getFieldDecorator('vehicleName', {
          })(<Input
            placeholder="Voyage Name"
            style={{ width: '100%' }}
            disabled={isPrefillingLastActivity}
          />)}
        </Form.Item>
      </Col>
      <Col span={10} style={{ paddingLeft: '6.5px' }}>
        <Form.Item className={classes.formItem}>
          {form.getFieldDecorator('vehicleNumber', {})(
            <Input
              placeholder="Voyage Number"
              style={{ width: '100%' }}
              disabled={isPrefillingLastActivity}
            />)}
        </Form.Item>
      </Col>
    </Row>;
  };

  const renderVehicleType = () => {
    return <Row>
      <Col
        span={4}
        className={classes.formLabel}>
        Vehicle
      </Col>
      <Col span={20}>
      <Form.Item className={classes.formItem}>
        {form.getFieldDecorator('vehicleType', {
        })(<Select
            showSearch
            disabled={isPrefillingLastActivity}
            style={{ width: '100%' }}
            placeholder="Select Vehicle"
            optionFilterProp="children"
            filterOption={(input, option) => true}
            onChange={value =>  setState({ isVoyageDetails: value === 'Vessel' })}
          >
            {staticData.vehicleTypes.map(item =>
              <Option
                value={item}
                key={item}>
                {item}
              </Option>,
            )}
          </Select>)}
        </Form.Item>
      </Col>
    </Row>;
  };

  const renderFooter = () => {
    return <Button
      key="submit"
      type="primary"
      style={{
        backgroundColor: tokens.colors.primary,
        border: tokens.colors.primary,
        borderRadius: tokens.radius.md,
        textShadow: 'none',
      }}
      className={classes.modalSubmitButton}
      loading = {confirmLoading}
      onClick={checkForErrors}>
      Add
    </Button>;
  };

  function getShipmentType() {
    switch (shipmentType) {
      case SHIPMENT_TYPE.CN: return ' Container';
      default: return ` ${shipmentType}`;
    }
  }

  const renderHeader = () => {
    return <p className={classes.modalBodyTitle}>
      Adding tracking event details for
      {getShipmentType()} {shipmentNumber} {shipmentShippingLine.name && `(${shipmentShippingLine.name})`}
    </p>;
  };

  const renderActivityDate = () => {
    return <Row>
      <Col span={4}
        className={classes.formLabel}>
        Date
        <span className={classes.required}>*</span>
      </Col>
      <Col span={20}>
        <Form.Item className={classes.formItem}>
          {form.getFieldDecorator('activityDate', {
            rules: [{ required: true, message: 'Please select date' }],
          })(<DatePicker 
              showTime
              disabledDate={disabledFutureDate}
              allowClear={false}
              format="DD MMM YYYY | hh:mm A"
              style={{ width: '100%' }}
              placeholder="Select Date"
            />)}
        </Form.Item>
      </Col>
    </Row>;
  };

  const renderLocation = () => {
    return <Row>
      <Col span={4}
        className={classes.formLabel}>
        Location
        <span className={classes.required}>*</span>
      </Col>
      <Col span={6}>
        <Form.Item className={classes.formItem}>
          {form.getFieldDecorator('locationType', {
            rules: [{ required: true, message: 'Please select Type' }],
          })(<Select
              disabled={isPrefillingLastActivity ? true : false}
              showSearch
              style={{ width: '90%' }}
              placeholder="Type"
              optionFilterProp="children"
              filterOption={(input, option) => true}
              dropdownMatchSelectWidth={false}
            >
            {activityMaster?.LocationTypeList?.map(item =>
              <Option
                value={item.name}
                key={item.name}>
                {item.name}
              </Option> || [])}
          </Select>)}
        </Form.Item>
      </Col>
      <Col span={14}>
        <Form.Item className={classes.formItem}>{form.getFieldDecorator('location', {
          rules: [{ required: true, message: 'Please select Location' }],
        })(<SearchPorts
            placeholder="Location"
            style={{ width: '100%' }}
            disabled={isPrefillingLastActivity}
          />)}
        </Form.Item>
      </Col>
    </Row>;
  };

  const renderActivity = () => {
    return <Row style={{ marginTop: '15px' }}>
      <Col span={4}
        className={classes.formLabel}>
        Activity
        <span className={classes.required}>*</span>
      </Col>
      <Col span={20}>
        <Form.Item className={classes.formItem}>
          {form.getFieldDecorator('activityType', {
            rules: [{ required: true, message: 'Please select Activity' }],
          })(<Select
            showSearch
            style={{ width: '100%' }}
            placeholder="Select Activity"
            optionFilterProp="children"
            filterOption={(input, option) => true}
          >
            {activityMaster?.activityList?.map(item =>
              <Option
                value={item}
                key={item}>
                {item}
              </Option>,
            )}
        </Select>)}
      </Form.Item>
      </Col>
    </Row>;
  };

  const renderForm = () => {
    return <Form>
      {renderActivity()}
      {renderLocation()}
      {renderActivityDate()}
      {renderVehicleType()}
      {renderVoyage()}
    </Form>;
  };

  return <Modal
    title = "Add Tracking Events"
    visible = {visible}
    onCancel = {() => onClose()}
    className = {classes.modal}
    footer={[renderFooter()]}
  >
    {renderHeader()}
    {renderForm()}
    {renderMessage()}
  </Modal>;
};

const mapStateToProps = ({ staticData, activityMasterData }) => ({
  staticData,
  activityMasterData,
});

const mapDispatchToProps = dispatch => (bindActionCreators({
  fetchManualTrackingMaster,
}, dispatch));

export default injectSheet(styles)(connect(mapStateToProps, mapDispatchToProps)(Form.create({})(
  AddManualTrackingEventModal)));
